import { autoUpdate, computePosition, flip, offset, shift, VirtualElement } from "@floating-ui/dom";

export type MenuText = string | (() => string);
export type MenuBool = boolean | (() => boolean);

export interface ContextMenuItem {
    text: MenuText;
    disabled?: MenuBool;
    onClick?: () => void | Promise<void>;
    submenu?: ContextMenuItem[];
}

export interface ContextMenuModel {
    items: ContextMenuItem[];
}

interface NormalizedItem {
    label: string;
    disabled: boolean;
    onClick?: () => void | Promise<void>;
    submenu?: NormalizedItem[];
}

export class ContextMenu
{
    private mainEl: HTMLDivElement | null = null;
    private subEl: HTMLDivElement | null = null;
    private cleanupMain: (() => void) | null = null;
    private cleanupSub: (() => void) | null = null;
    private boundClose?: (e: MouseEvent) => void;

    constructor(private readonly model: ContextMenuModel)
    {}

    open(anchorEvent: MouseEvent): void
    {
        this.close();

        this.mainEl = this.createMenuContainer();
        document.body.appendChild(this.mainEl);

        const ref = this.createVirtualReference(anchorEvent);

        this.cleanupMain = autoUpdate(ref, this.mainEl, () =>
        {
            computePosition(ref, this.mainEl, {
                placement: "right-start",
                middleware: [offset(4), flip(), shift()]
            }).then(({ x, y }) =>
            {
                Object.assign(this.mainEl.style, {
                    left: `${x}px`,
                    top: `${y}px`
                });
            });
        });

        const items = this.model.items
            .map(i => this.normalize(i))
            .filter(Boolean);

        items.forEach(i => this.renderRow(this.mainEl, i));

        this.boundClose = e =>
        {
            if (
                !this.mainEl?.contains(e.target as Node) &&
                !this.subEl?.contains(e.target as Node)
            )
            {
                this.close();
            }
        };

        setTimeout(() => document.addEventListener("mousedown", this.boundClose), 0);
    }

    close(): void
    {
        this.subEl?.remove();
        this.mainEl?.remove();
        this.cleanupSub?.();
        this.cleanupMain?.();

        if (this.boundClose)
        {
            document.removeEventListener("mousedown", this.boundClose);
            this.boundClose = undefined;
        }

        this.subEl = null;
        this.mainEl = null;
        this.cleanupSub = null;
        this.cleanupMain = null;
    }

    destroy(): void
    {
        this.close();
    }

    private createMenuContainer(): HTMLDivElement
    {
        const el = document.createElement("div");
        Object.assign(el.style, {
            position: "absolute",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            zIndex: "999",
            boxShadow: "0 3px 12px rgba(0,0,0,0.20)",
            padding: "3px 0"
        });
        return el;
    }

    private createVirtualReference(e: MouseEvent): VirtualElement
    {
        return {
            getBoundingClientRect: () =>
            {
                const top = e.pageY - window.scrollY;
                const left = e.pageX - window.scrollX;
                return {
                    width: 0,
                    height: 0,
                    top: top,
                    right: left,
                    bottom: top,
                    left: left,
                    x: left,
                    y: top,
                };
            },
        };
    }

    private evalText(v: MenuText): string
    {
        return typeof v === "function" ? v() : v;
    }

    private evalBool(v: MenuBool | undefined): boolean
    {
        return typeof v === "function" ? v() : !!v;
    }

    private normalize(item: ContextMenuItem): NormalizedItem | null
    {
        const label = this.evalText(item.text);
        if (!label)
        {
            return null;
        }

        const submenu = item.submenu?.map(s => this.normalize(s));

        return {
            label,
            disabled: this.evalBool(item.disabled),
            onClick: item.onClick,
            submenu
        };
    }

    private renderRow(parent: HTMLElement, item: NormalizedItem): void
    {
        const row = document.createElement("div");
        row.style.padding = "3px 6px";
        row.style.display = "flex";
        row.style.justifyContent = "space-between";
        row.style.cursor = item.disabled ? "default" : "pointer";

        const label = document.createElement("span");
        label.style.marginLeft = "10px";
        label.style.marginRight = "6px";
        label.textContent = item.label;
        row.appendChild(label);

        if (item.submenu?.length)
        {
            const arrow = document.createElement("span");
            arrow.textContent = "►";
            row.appendChild(arrow);
        }

        if (item.disabled)
        {
            row.style.color = "#aaa";
        }
        else
        {
            row.addEventListener("mouseenter", () =>
            {
                row.style.background = "#eee";
                if (item.submenu)
                {
                    this.openSubmenu(row, item.submenu);
                }
            });

            row.addEventListener("mouseleave", () => (row.style.background = ""));

            row.addEventListener("click", async () =>
            {
                if (!item.submenu)
                {
                    this.close();
                    await item.onClick?.();
                }
            });
        }

        parent.appendChild(row);
    }

    private openSubmenu(anchor: HTMLElement, items: NormalizedItem[]): void
    {
        this.closeSubmenu();

        this.subEl = this.createMenuContainer();
        document.body.appendChild(this.subEl);

        items.forEach(i => this.renderRow(this.subEl, i));

        this.subEl.addEventListener("mouseenter", () =>
        {
            // prevents premature close
        });

        this.subEl.addEventListener("mouseleave", () =>
        {
            const hovered = document.querySelector(":hover");
            if (!anchor.contains(hovered))
            {
                this.closeSubmenu();
            }
        });

        this.cleanupSub = autoUpdate(anchor, this.subEl, () =>
        {
            computePosition(anchor, this.subEl, {
                placement: "right-start",
                middleware: [offset(4), flip(), shift()]
            }).then(({ x, y }) =>
            {
                Object.assign(this.subEl.style, {
                    left: `${x}px`,
                    top: `${y}px`
                });
            });
        });
    }

    private closeSubmenu(): void
    {
        this.subEl?.remove();
        this.cleanupSub?.();
        this.subEl = null;
        this.cleanupSub = null;
    }
}
